import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import dayjs from "dayjs";
import { MessageList as MessageListElement } from "react-chat-elements";
import { useSelector } from "react-redux";
import Linkify from "react-linkify";
import {
  agentsSelector,
  conversationTypeSelector,
  crmIsoSettingSelector, // [crm-iso]
  errorCodesSelector,
  isActiveISOSettingSelector, // [crm-iso]
  userSelector,
} from "appredux/selectors";
import { messageStatuses, messageTypes } from "utils/static";
import { Text } from "components/UI";
import { Button, FloatButton, Image, Space } from "antd";
import {
  ArrowDownOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import ContactMessage from "./ContactMessage";
import UnsupportedMessage from "./UnsupportedMessage";
import Scrollbars from "react-custom-scrollbars-2";
import FileMessage, { FileIcon } from "./FileMessage";
import {
  classNames,
  convertWhatsAppFormatToHTML,
  highlightSearchInHTML,
} from "utils/helper";
import { formatTime } from "utils/formatting";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { groupBy, isEmpty, throttle } from "lodash-es";
import DownloadVideoBtn from "../button/DownloadVideo";
import ButtonsPreview from "components/apps/Templates/ButtonsPreview";

const bottomThreshold = 10;
const messagesPerPage = 30;

const getInhouseTemplateButtons = (templatePayload) => {
  if (!templatePayload) return [];
  try {
    const payload =
      typeof templatePayload === "string"
        ? JSON.parse(templatePayload)
        : templatePayload;
    return payload?.buttons?.buttons || [];
  } catch {
    return [];
  }
};

const MessageList = forwardRef(
  (
    {
      messages,
      contact,
      isHistory = false,
      onReply,
      messageListProps = {},
      search,
      showFloatButton = true,
      random = false,
      agentRandomList = [],
      showLoadMoreButton = false,
      isUsingManualPagination = true,
      onLoadMore = () => {},
      isLoadingLoadMore = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // [crm-iso] TEARDOWN: kembalikan ke `const agents = useSelector(agentsSelector);`
    const rawAgents = useSelector(agentsSelector);
    const errorCodes = useSelector(errorCodesSelector);
    const user = useSelector(userSelector);
    const conversation_type = useSelector(conversationTypeSelector);
    // [crm-iso] START — hidden feature (lihat CrmIsoSetting/TEARDOWN.md).
    // Hapus blok ini + `transformSystemText`, lalu revert `rawAgents` -> `agents`.
    const isActiveISO = useSelector(isActiveISOSettingSelector);
    const crmIsoSetting = useSelector(crmIsoSettingSelector);

    // Bila fitur CRM ISO aktif untuk agen ini, timpa nama agen yang tergabung
    // dalam pemetaan dengan nama AGEN UTAMA-nya. Contoh: pemetaan X = {X, Y, Z}
    // → Y & Z ditampilkan sebagai "X". Karena `getTitle` & `row.title` sama-sama
    // memakai `agents.find(...).name`, cukup transform di satu tempat ini.
    const agents = useMemo(() => {
      if (!isActiveISO || isEmpty(crmIsoSetting?.agent_name_changes)) {
        return rawAgents;
      }
      const mainNameByAgentId = new Map();
      crmIsoSetting.agent_name_changes.forEach((mapping) => {
        (mapping.aliases || []).forEach((alias) => {
          mainNameByAgentId.set(alias.id, mapping.name);
        });
      });
      if (!mainNameByAgentId.size) return rawAgents;
      return rawAgents.map((agent) =>
        mainNameByAgentId.has(agent.id)
          ? {
              ...agent,
              name: mainNameByAgentId.get(agent.id),
              // Simpan nama asli agar bisa dipakai untuk mengganti teks system message.
              original_name: agent.name,
            }
          : agent,
      );
    }, [rawAgents, isActiveISO, crmIsoSetting]);

    // Untuk system message (type_id 70) seperti "agent Lisya Kha mengambil
    // conversation ID:1171": bila agen tsb ter-remap, ganti nama aslinya di teks
    // dengan nama agen utama (name) via regex.
    const transformSystemText = useCallback(
      (message) => {
        const text = message.message_text || "";
        if (message.type_id !== 70) return text;
        const agent = agents.find((a) => a.id === message.agent_id);
        if (!agent?.original_name || agent.original_name === agent.name) {
          return text;
        }
        const escaped = agent.original_name.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&",
        );
        return text.replace(new RegExp(escaped, "g"), agent.name);
      },
      [agents],
    );
    // [crm-iso] END

    const lastMessage = useRef();
    const messageRefs = useRef({});
    const scrollContainerRef = useRef();
    const isScrolling = useRef(false);
    const scrollPosition = useRef(0);
    const hasMoreMessages = useRef(false);
    const pendingReplyScrollId = useRef(null);

    const [previewImage, setPreviewImage] = useState({ show: false, uri: "" });
    const [showBackToBottomButton, setShowBackToBottomButton] = useState(false);
    const [page, setPage] = useState(1);

    const handleLoadMore = useCallback(() => {
      if (!isUsingManualPagination) return;

      if (!isScrolling.current) {
        isScrolling.current = true;

        // Simpan scroll position sebelum load
        if (scrollContainerRef.current?.view) {
          scrollPosition.current = scrollContainerRef.current.view.scrollTop;
        }

        const nextPage = page + 1;
        setPage(nextPage);

        // Kembalikan scroll position setelah state update
        setTimeout(() => {
          if (scrollContainerRef.current?.view) {
            scrollContainerRef.current.view.scrollTop = scrollPosition.current;
          }
          isScrolling.current = false;
        }, 0);
      }
    }, [page, isUsingManualPagination]);

    const messageScrollHandler = throttle((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isShow = scrollTop < scrollHeight - clientHeight - bottomThreshold;

      if (showBackToBottomButton !== isShow) {
        setShowBackToBottomButton(isShow);
      }

      // Cek apakah sudah mencapai bagian atas untuk load more
      if (
        scrollTop < 100 &&
        !isScrolling.current &&
        hasMoreMessages.current &&
        isUsingManualPagination
      ) {
        // Scroll ke bawah dan load more
        if (scrollContainerRef.current?.view) {
          scrollContainerRef.current.view.scrollTop = 150;
          handleLoadMore();
        }
      }
    }, 100);

    // Reset state ketika percakapan berubah
    useEffect(() => {
      // Reset page ke 1
      setPage(1);

      // Reset scroll position ke bawah
      if (scrollContainerRef.current) {
        scrollToBottomHandler();
      }
      // Reset pending scroll
      pendingReplyScrollId.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact?.id]);

    // Handle scroll ke reply message setelah page berubah
    useEffect(() => {
      if (pendingReplyScrollId.current) {
        const replyToMessageId = pendingReplyScrollId.current;

        // Tunggu DOM update
        setTimeout(() => {
          const messageElement = messageRefs.current[replyToMessageId];
          if (messageElement) {
            const scrollContainer = scrollContainerRef.current?.view;

            // Cari elemen parent yang terlihat
            let targetElement = messageElement;
            let parent = messageElement.parentElement;

            while (parent && parent !== scrollContainer) {
              if (
                parent.classList &&
                (parent.classList.contains("rce-mbox") ||
                  parent.classList.contains("message"))
              ) {
                targetElement = parent;
                break;
              }
              parent = parent.parentElement;
            }

            if (scrollContainer) {
              // Hitung posisi dan scroll
              const containerRect = scrollContainer.getBoundingClientRect();
              const elementRect = targetElement.getBoundingClientRect();
              const scrollTop =
                scrollContainer.scrollTop +
                (elementRect.top - containerRect.top) -
                20;

              scrollContainer.scrollTo({
                top: scrollTop,
                behavior: "smooth",
              });
            } else {
              // Fallback ke scrollIntoView
              targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }

            // Reset pending scroll
            pendingReplyScrollId.current = null;
          }
        }, 300);
      }
    }, [page]);

    const previewImageVisibleChangeHandler = useCallback((value) => {
      setPreviewImage((prevState) => {
        if (!value) {
          return { show: false, uri: "" };
        }
        return { ...prevState, show: true };
      });
    }, []);

    const scrollToBottomHandler = useCallback(() => {
      if (agentRandomList && agentRandomList.length > 0) {
        return;
      }
      lastMessage.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, [agentRandomList]);

    const getTitle = useCallback(
      (message) => {
        let title = "";
        if (message.is_out === 0) {
          title = random ? "Customer" : contact?.name || "";
        } else if (message.agent_id === user.id) {
          title = "You";
        } else {
          const selectedAgents =
            random && agentRandomList.length > 0 ? agentRandomList : agents;
          const agent = selectedAgents.find(
            (agent) => agent.id === message.agent_id,
          );
          title = agent?.name
            ? agent?.name
            : message.agent_id === 0
              ? "Bot"
              : "Unnamed Agent";
          if (message.extendedMessage === 1 && message.agent_id !== 0) {
            title = "Agent X";
          }
        }
        return title;
      },
      [agents, contact?.name, user.id, random, agentRandomList],
    );

    const handleClickMessage = useCallback((value) => {
      if (value.type === "photo") {
        setPreviewImage({ show: true, uri: value.data?.uri });
      }
    }, []);

    const handleReplyClick = useCallback(
      (value) => {
        const message = messages.find((row) => row.id === value.id);
        if (message) {
          const replyData = { ...message, title: getTitle(message) };
          onReply(replyData);
        }
      },
      [getTitle, messages, onReply],
    );

    const handleReplyMessageClick = useCallback(
      (value) => {
        // Scroll ke pesan yang direply
        const replyToMessageId = value.reply_to_message_id;

        if (!replyToMessageId) return;

        // Cari pesan di allMessages untuk mendapatkan index-nya
        const allMessages = [...messages].sort(
          (a, b) => new Date(a.message_time) - new Date(b.message_time),
        );

        const targetMessageIndex = allMessages.findIndex(
          (msg) => msg.id === replyToMessageId,
        );

        if (targetMessageIndex === -1) {
          // Pesan tidak ditemukan di semua messages
          console.warn("Reply message not found:", replyToMessageId);
          return;
        }

        // Hitung berapa page yang diperlukan untuk menampilkan pesan ini
        const requiredPage = Math.ceil(
          (allMessages.length - targetMessageIndex) / messagesPerPage,
        );

        // Cek apakah pesan sudah ada di displayedMessages
        const startIndex = Math.max(
          0,
          allMessages.length - page * messagesPerPage,
        );
        const displayedMessages = allMessages.slice(startIndex);
        const isMessageDisplayed = displayedMessages.some(
          (msg) => msg.id === replyToMessageId,
        );

        if (!isMessageDisplayed && requiredPage > page) {
          // Pesan belum di-load, perlu load more
          // Simpan ID pesan yang perlu di-scroll setelah page berubah
          pendingReplyScrollId.current = replyToMessageId;
          // Set page ke requiredPage untuk load pesan tersebut
          setPage(requiredPage);
          // Scroll akan dihandle oleh useEffect yang memantau perubahan page
        } else {
          // Pesan sudah di-load, langsung scroll
          const messageElement = messageRefs.current[replyToMessageId];
          if (messageElement) {
            const scrollContainer = scrollContainerRef.current?.view;

            if (scrollContainer) {
              // Cari elemen parent yang terlihat
              let targetElement = messageElement;
              let parent = messageElement.parentElement;

              while (parent && parent !== scrollContainer) {
                if (
                  parent.classList &&
                  (parent.classList.contains("rce-mbox") ||
                    parent.classList.contains("message"))
                ) {
                  targetElement = parent;
                  break;
                }
                parent = parent.parentElement;
              }

              // Hitung posisi dan scroll
              setTimeout(() => {
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = targetElement.getBoundingClientRect();
                const scrollTop =
                  scrollContainer.scrollTop +
                  (elementRect.top - containerRect.top) -
                  20;

                scrollContainer.scrollTo({
                  top: scrollTop,
                  behavior: "smooth",
                });
              }, 100);
            } else {
              // Fallback ke scrollIntoView
              messageElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        }
      },
      [messages, page],
    );
    const renderMessageContent = useCallback((message, _search) => {
      const messageText =
        typeof message.message_text === "string" ? message.message_text : "";

      const cleanedMessage = messageText
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      let htmlString = convertWhatsAppFormatToHTML(cleanedMessage);

      if (!isEmpty(_search)) {
        htmlString = highlightSearchInHTML(htmlString, _search);
      }
      const processedHtml = htmlString.replace(/<br\/>/g, "&nbsp;<br/>");

      const safeHtml = DOMPurify.sanitize(processedHtml);

      const htmlContent = parse(safeHtml);

      return (
        <Linkify
          componentDecorator={(decoratedHref, decoratedText, key) => (
            <a
              key={key}
              href={decoratedHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {decoratedText}
            </a>
          )}
        >
          {htmlContent}
        </Linkify>
      );
    }, []);

    const formattedMessages = useMemo(() => {
      {
        // Urutkan semua pesan dari yang lama ke baru tanpa memfilter pesan sistem
        const allMessages = messages.sort(
          (a, b) => new Date(a.message_time) - new Date(b.message_time),
        );

        // Hitung total halaman
        const totalPages = Math.ceil(allMessages.length / messagesPerPage);
        hasMoreMessages.current = page < totalPages;

        // Ambil pesan dari yang paling lama sesuai halaman
        const startIndex = Math.max(
          0,
          allMessages.length - page * messagesPerPage,
        );
        const displayedMessages = allMessages.slice(startIndex);

        const groupMessages = groupBy(
          isUsingManualPagination ? displayedMessages : allMessages,
          (message) => dayjs(message.message_time).format("YYYY-MM-DD"),
        );

        const renderMessages = [];

        // Tampilkan tombol load more jika masih ada pesan yang belum ditampilkan
        if (hasMoreMessages.current && isUsingManualPagination) {
          renderMessages.push({
            id: "load-more-button",
            text: (
              <div className="py-2">
                <Button
                  className="text-blue-500"
                  type="text"
                  onClick={handleLoadMore}
                >
                  {t("load_more_messages")}
                </Button>
              </div>
            ),
            type: "system",
            className: "flex justify-center my-2",
          });
        }
        // hit api untuk prev conversations
        if (!isUsingManualPagination && showLoadMoreButton) {
          renderMessages.push({
            id: "load-more-button",
            text: (
              <div className="py-2">
                <Button
                  className="text-blue-500"
                  type="text"
                  onClick={onLoadMore}
                  loading={isLoadingLoadMore}
                >
                  {t("load_more_messages")}
                </Button>
              </div>
            ),
            type: "system",
            className: "flex justify-center my-2",
          });
        }

        // Urutkan tanggal dari yang lama ke baru
        const sortedDates = Object.keys(groupMessages).sort(
          (a, b) => new Date(a) - new Date(b),
        );

        sortedDates.forEach((key) => {
          renderMessages.push({
            id: key,
            text: dayjs(key).format("MMMM DD, YYYY"),
            type: "system",
            className: "text-sm uppercase my-1",
          });

          if (Array.isArray(groupMessages[key])) {
            // Urutkan pesan dalam satu hari dari yang lama ke baru
            const sortedMessages = groupMessages[key].sort(
              (a, b) => new Date(a.message_time) - new Date(b.message_time),
            );

            sortedMessages.forEach((message) => {
              if (messageTypes.SYSTEM.includes(message.type_id)) {
                renderMessages.push({
                  id: key,
                  text:
                    // [crm-iso] TEARDOWN: ganti `transformSystemText(message)` -> `message.message_text`
                    transformSystemText(message) +
                    "\n" +
                    dayjs(message.message_time).format("DD-MM-YYYY HH:mm:ss"),
                  type: "system",
                  className: " message-system",
                });
                return;
              }

              const row = {
                id: message.id,
                date: dayjs(message.message_time).toDate(),
                dateString: formatTime(message.message_time),
              };

              if (!isEmpty(message.message_text)) {
                row.text = <Text>{renderMessageContent(message, search)}</Text>;
              }

              if (message.is_out === 0) {
                row.position = "left";
                row.title = random ? "Customer" : contact?.name;
              } else {
                const selectedAgents =
                  random && agentRandomList.length > 0
                    ? agentRandomList
                    : agents;
                const agent = selectedAgents.find(
                  (agent) => agent.id === message.agent_id,
                );
                row.position = "right";
                row.title = agent?.name
                  ? agent?.name
                  : message.agent_id === 0
                    ? "Bot"
                    : "Unnamed Agent";
                if (message.extendedMessage === 1 && message.agent_id !== 0) {
                  row.title = "Agent X";
                }
              }

              if (
                message.type_id === messageTypes.TEXT ||
                message.type_id === messageTypes.TEMPLATE
              ) {
                row.type = "text";

                // Template channel inhouse terkirim sebagai pesan TEKS; tombolnya
                // dibawa di `template_payload.buttons`. Template WA tetap tipe
                // TEMPLATE, jadi tidak masuk ke sini.
                const inhouseTemplateButtons =
                  message.type_id === messageTypes.TEXT
                    ? getInhouseTemplateButtons(message.template_payload)
                    : [];
                if (!isEmpty(inhouseTemplateButtons)) {
                  row.text = (
                    <>
                      {row.text}
                      <ButtonsPreview
                        buttons={inhouseTemplateButtons}
                        className="mt-2 border-t border-gray-300"
                        clickable
                      />
                    </>
                  );
                }

                if (message.type_id === messageTypes.TEMPLATE) {
                  const dataTemplatePayload = message.template_payload
                    ? JSON.parse(message.template_payload)
                    : message.template_header_params
                      ? { header: message.template_header_params }
                      : "";

                  if (!isEmpty(dataTemplatePayload.header)) {
                    const key = Object.keys(dataTemplatePayload.header)[0];
                    if (key === "image") {
                      row.data = {
                        uri: dataTemplatePayload.header.image,
                        width: 300,
                        height: "100%",
                        status: { click: true, download: true },
                      };
                      row.type = "photo";

                      row.renderAddCmp = (
                        <Image
                          width={200}
                          className="hidden"
                          src={dataTemplatePayload.header.image}
                          preview={{
                            visible:
                              previewImage.show &&
                              previewImage.uri ===
                                dataTemplatePayload.header.image,
                            src: dataTemplatePayload.header.image,
                            onVisibleChange: previewImageVisibleChangeHandler,
                          }}
                        />
                      );
                    } else if (key === "document") {
                      const manipulateMessage = {
                        ...message,
                        media_url: dataTemplatePayload.header.document,
                      };
                      if (!manipulateMessage.media_document_filename) {
                        manipulateMessage.media_document_filename =
                          dataTemplatePayload.header.document;
                      }
                      row.text = (
                        <FileMessage
                          message={manipulateMessage}
                          text={renderMessageContent(message, search)}
                        />
                      );
                      row.type = "text";
                    }
                  }
                }
              } else if (
                message.type_id === messageTypes.IMAGE ||
                message.type_id === messageTypes.STICKER
              ) {
                row.data = {
                  uri: message.media_url,
                  width: 300,
                  height: "100%",
                  status: { click: true, download: true },
                };
                row.type = "photo";

                row.renderAddCmp = (
                  <Image
                    width={200}
                    className="hidden"
                    src={message.media_url}
                    preview={{
                      visible:
                        previewImage.show &&
                        previewImage.uri === message.media_url,
                      src: message.media_url,
                      onVisibleChange: previewImageVisibleChangeHandler,
                    }}
                  />
                );
              } else if (message.type_id === messageTypes.DOCUMENT) {
                row.text = <FileMessage message={message} />;
                row.type = "text";
              } else if (message.type_id === messageTypes.VIDEO) {
                row.text = (
                  <div>
                    <DownloadVideoBtn
                      messageId={message.id}
                      fileName={message.media_document_filename}
                    />
                    <br />
                    {message.message_text}
                  </div>
                );
                row.data = {
                  videoURL: message.media_url,
                  status: {
                    click: true,
                    loading: 0,
                    download: true,
                  },
                };
                row.type = "video";
              } else if (message.type_id === messageTypes.AUDIO) {
                row.data = {
                  audioURL: message.media_url,
                };
                row.type = "audio";
              } else if (message.type_id === messageTypes.CONTACTS) {
                row.type = "text";
                row.text = (
                  <ContactMessage messageText={message.message_text} />
                );
              } else if (message.type_id === messageTypes.UNSUPPORT) {
                row.type = "text";
                row.text = <UnsupportedMessage />;
              } else if (message.type_id === messageTypes.LOCATION) {
                row.type = "location";
                const parseMessageText = JSON.parse(message.message_text);
                row.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
                row.src =
                  "https://cdn.pixabay.com/photo/2016/03/22/04/23/map-1272165_1280.png";
                row.data = {
                  latitude: parseMessageText?.latitude,
                  longitude: parseMessageText?.longitude,
                };
                row.text = "";
                row.href = `https://www.google.com/maps/search/?api=1&query=${parseMessageText?.latitude},${parseMessageText?.longitude}&zoom=14`;
              }

              if (message.status_id === messageStatuses.FAILED) {
                row.className = `text-gray-400`;
              } else if (message.status_id === messageStatuses.SENT) {
                row.status = "sent";
              } else if (message.status_id === messageStatuses.READ) {
                row.status = "read";
              } else if (message.status_id === messageStatuses.DELIVERED) {
                row.status = "received";
              }

              if (!isEmpty(message.quote) && message.quote != "[]") {
                const quote = JSON.parse(message.quote);
                const reply = {
                  titleColor: "#8717ae",
                  message: quote.message_text,
                };
                reply.title = getTitle(quote);

                if (
                  !isEmpty(quote.media_url) &&
                  (quote.type_id === messageTypes.IMAGE ||
                    quote.type_id === messageTypes.STICKER)
                ) {
                  reply.photoURL = quote.media_url;
                  reply.message = (
                    <div className="flex flex-row space-x-1">
                      <FileIcon extension="jpeg" className="h-[14px]" />
                      <Text className="text-[#5a5a5a] text-xs">Photo</Text>
                    </div>
                  );
                }

                if (
                  quote.type_id === messageTypes.DOCUMENT ||
                  quote.type_id === messageTypes.VIDEO ||
                  quote.type_id === messageTypes.AUDIO
                ) {
                  if (quote.type_id === messageTypes.VIDEO) {
                    quote.media_document_filename = "Video";
                  } else if (quote.type_id === messageTypes.AUDIO) {
                    quote.media_document_filename = "Audio";
                  }
                  reply.message = <FileMessage isReply message={quote} />;
                }
                row.reply = reply;
              }

              row.replyButton =
                conversation_type === "unassigned" ||
                conversation_type === "all" ||
                conversation_type === "assigned"
                  ? false
                  : true;
              row.reply_to_message_id = message.reply_to_message_id;

              row.renderAddCmp = (
                <div
                  ref={(el) => (messageRefs.current[message.id] = el)}
                  className="invisible"
                >
                  {row.renderAddCmp}
                </div>
              );

              if (message.status_id === messageStatuses.FAILED) {
                const error = errorCodes.find(
                  (row) => row.id === message.error_code_id,
                );
                row.className = `text-gray-400`;
                row.renderAddCmp = (
                  <>
                    <Space className="w-full justify-end pt-2 pr-4">
                      <ExclamationCircleOutlined className="text-yellow-500" />
                      <Text className="text-red-400 italic text-sm">
                        {error?.description}
                      </Text>
                    </Space>
                  </>
                );
              }

              renderMessages.push(row);
            });
          }
        });
        return renderMessages;
      }
    }, [
      messages,
      page,
      handleLoadMore,
      t,
      conversation_type,
      renderMessageContent,
      transformSystemText, // [crm-iso]
      contact?.name,
      agents,
      previewImage.show,
      previewImage.uri,
      previewImageVisibleChangeHandler,
      getTitle,
      errorCodes,
      search,
      random,
      agentRandomList,
      isUsingManualPagination,
      onLoadMore,
      showLoadMoreButton,
      isLoadingLoadMore,
    ]);

    useImperativeHandle(ref, () => {
      return {
        onScrollToBottom: scrollToBottomHandler,
        getScrollContainer: () => scrollContainerRef.current?.view,
      };
    }, [scrollToBottomHandler]);

    return (
      <>
        <Scrollbars
          className={classNames(
            isHistory ? "h-full" : "h-full",
            "bg-[#DAD3CC] overscroll-y-contain order-2 flex-1 container-scrollbar-custom-style",
          )}
          autoHide
          onScroll={messageScrollHandler}
          ref={scrollContainerRef}
        >
          <MessageListElement
            className="pb-4"
            dataSource={formattedMessages}
            onOpen={handleClickMessage}
            onReplyClick={handleReplyClick}
            onReplyMessageClick={handleReplyMessageClick}
            {...messageListProps}
          />
          <div className="mt-1" ref={lastMessage} />
        </Scrollbars>

        {showFloatButton && showBackToBottomButton && (
          <FloatButton
            onClick={scrollToBottomHandler}
            className="mb-24 right-4"
            icon={<ArrowDownOutlined />}
          />
        )}
      </>
    );
  },
);

const Component = memo(MessageList);
MessageList.displayName = "MessageList";

export default Component;
